// ============================================================
// Chess Arena — Battle HUD
// ============================================================
// Phaser-rendered HUD overlay within BattleScene.
// Top bar: opponent info + match timer.
// Bottom bar: next-piece preview, unified action buttons
//             (tools + abilities), spawn timer.
// ============================================================

import Phaser from 'phaser';
import {
  LevelConfig,
  PieceType,
  OpponentConfig,
} from '../core/types';
import eventBus from '../core/eventBus';
import { ToolManager } from '../systems/toolManager';
import { AbilityManager } from '../systems/abilityManager';
import { playerState } from '../core/playerState';

// --- Layout constants ---

const TOP_BAR_HEIGHT = 100;
const BOTTOM_BAR_HEIGHT = 200;
const ACTION_BTN_RADIUS = 46;
const ACTION_BTN_GAP = 12;
const ACTION_BTN_DIAMETER = ACTION_BTN_RADIUS * 2;
const NEXT_CARD_SIZE = 76;
const TIMER_FLASH_THRESHOLD = 10; // seconds

// Color palette for each action button type
const ACTION_COLORS: Record<string, { bg: number; border: number }> = {
  reroll:        { bg: 0x4CAF50, border: 0x388E3C },
  instant_spawn: { bg: 0x26A69A, border: 0x00897B },
  bomb:          { bg: 0xE53935, border: 0xC62828 },
  shield:        { bg: 0x1E88E5, border: 0x1565C0 },
  freeze:        { bg: 0x29B6F6, border: 0x0288D1 },
  lightning:     { bg: 0x7B1FA2, border: 0x6A1B9A },
};
const DEFAULT_ACTION_COLOR = { bg: 0x5C6BC0, border: 0x3949AB };

interface ActionEntry {
  id: string;
  type: 'tool' | 'ability';
  displayName: string;
  iconKey: string;
}

export class BattleHUD {
  private scene: Phaser.Scene;
  private levelConfig!: LevelConfig;
  private container!: Phaser.GameObjects.Container;

  // Top bar elements
  private portraitBg!: Phaser.GameObjects.Graphics;
  private portraitText!: Phaser.GameObjects.Text;
  private opponentText!: Phaser.GameObjects.Text;
  private ratingBg!: Phaser.GameObjects.Graphics;
  private ratingIcon!: Phaser.GameObjects.Image | null;
  private ratingText!: Phaser.GameObjects.Text;
  private timerBg!: Phaser.GameObjects.Graphics;
  private timerText!: Phaser.GameObjects.Text;
  private timerFlashing = false;

  // Bottom bar elements
  private bottomPanel!: Phaser.GameObjects.Graphics;
  private spawnTimerBg!: Phaser.GameObjects.Graphics;
  private spawnTimerText!: Phaser.GameObjects.Text;
  private spawnPreviewIcon: Phaser.GameObjects.Image | null = null;
  private nextCardBg!: Phaser.GameObjects.Graphics;
  private nextLabel!: Phaser.GameObjects.Text;

  // Unified action buttons (tools + abilities)
  private actionButtons: Phaser.GameObjects.Container[] = [];
  private toolManager: ToolManager | null = null;
  private abilityManager: AbilityManager | null = null;

  // Skip button (debug)
  private skipButton!: Phaser.GameObjects.Text;

  // Cached screen dimensions
  private screenW = 0;
  private screenH = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Initialisation
  // ------------------------------------------------------------------

  private highestLevelCompleted = 0;

  init(
    levelConfig: LevelConfig,
    highestLevelCompleted: number = 0,
    toolManager?: ToolManager,
    abilityManager?: AbilityManager,
  ): void {
    this.levelConfig = levelConfig;
    this.highestLevelCompleted = highestLevelCompleted;
    this.toolManager = toolManager ?? null;
    this.abilityManager = abilityManager ?? null;
    this.screenW = this.scene.cameras.main.width;
    this.screenH = this.scene.cameras.main.height;

    this.container = this.scene.add.container(0, 0);
    this.container.setDepth(100);

    this.createTopBar(levelConfig.opponent);
    this.createTimer(levelConfig.matchTimerSec);
    this.createBottomBar();
    this.createActionButtons();
  }

  // ------------------------------------------------------------------
  // Top bar
  // ------------------------------------------------------------------

  private createTopBar(opponent: OpponentConfig): void {
    const centerY = TOP_BAR_HEIGHT / 2 + 6;
    const portraitR = 38;
    const portraitX = 16 + portraitR;

    // Circular portrait frame — gold ring only, no dark outline
    this.portraitBg = this.scene.add.graphics();
    // Outer gold ring
    this.portraitBg.fillStyle(0x8B6914, 1);
    this.portraitBg.fillCircle(portraitX, centerY, portraitR + 4);
    this.portraitBg.fillStyle(0xC9A96E, 1);
    this.portraitBg.fillCircle(portraitX, centerY, portraitR + 1);
    // Inner dark fill
    this.portraitBg.fillStyle(0x2C1B0E, 1);
    this.portraitBg.fillCircle(portraitX, centerY, portraitR - 3);
    // Subtle inner highlight
    this.portraitBg.fillStyle(0x4A3222, 1);
    this.portraitBg.fillCircle(portraitX, centerY - 4, portraitR - 8);
    this.container.add(this.portraitBg);

    // Emoji portrait inside circle — large
    this.portraitText = this.scene.add.text(portraitX, centerY + 1, opponent.portrait, {
      fontSize: '44px',
    }).setOrigin(0.5, 0.5);
    this.container.add(this.portraitText);

    // Opponent name — chunky bold with dark stroke outline, left-aligned
    const nameX = portraitX + portraitR + 12;
    const maxNameWidth = this.screenW - nameX - 140; // leave room for timer
    this.opponentText = this.scene.add.text(nameX, centerY - 16, opponent.name.toUpperCase(), {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '28px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#1a0e05',
      strokeThickness: 5,
      maxLines: 1,
      wordWrap: { width: maxNameWidth + 50 },
    }).setOrigin(0, 0.5).setCrop(0, 0, maxNameWidth, 60);
    this.container.add(this.opponentText);

    // Trophy + rating: sunken capsule background with trophy icon and number
    const ratingY = centerY + 18;
    const ratingPillX = nameX - 6;
    const ratingPillW = 120;
    const ratingPillH = 34;

    this.ratingBg = this.scene.add.graphics();
    this.ratingBg.fillStyle(0x000000, 0.25);
    this.ratingBg.fillRoundedRect(ratingPillX, ratingY - ratingPillH / 2, ratingPillW, ratingPillH, ratingPillH / 2);
    this.container.add(this.ratingBg);

    this.ratingIcon = null;
    if (this.scene.textures.exists('icon_trophy')) {
      this.ratingIcon = this.scene.add.image(nameX + 10, ratingY, 'icon_trophy');
      this.ratingIcon.setDisplaySize(30, 30);
      this.container.add(this.ratingIcon);
    }

    this.ratingText = this.scene.add.text(nameX + 30, ratingY, `${opponent.rating}`, {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#fbbf24',
    }).setOrigin(0, 0.5);
    this.container.add(this.ratingText);
  }

  // ------------------------------------------------------------------
  // Timer
  // ------------------------------------------------------------------

  private createTimer(matchTimerSec: number | null): void {
    const centerY = TOP_BAR_HEIGHT / 2 + 6;
    const pillW = 110;
    const pillH = 46;
    const pillX = this.screenW - 14 - pillW;

    // Green capsule background
    this.timerBg = this.scene.add.graphics();
    this.redrawTimerCapsule(0x2E7D32, 0x1B5E20, 0x66BB6A);
    this.container.add(this.timerBg);

    this.timerText = this.scene.add.text(pillX + pillW / 2, centerY, '', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '30px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#1B5E20',
      strokeThickness: 4,
    }).setOrigin(0.5, 0.5);

    if (matchTimerSec === null) {
      this.timerText.setVisible(false);
      this.timerBg.setVisible(false);
    }

    this.container.add(this.timerText);
  }

  // ------------------------------------------------------------------
  // Bottom bar
  // ------------------------------------------------------------------

  private createBottomBar(): void {
    const trayTop = this.screenH - BOTTOM_BAR_HEIGHT;

    this.bottomPanel = this.scene.add.graphics();
    this.container.add(this.bottomPanel);

    // "NEXT" piece preview card — warm brown with gold border
    const cardX = 16 + NEXT_CARD_SIZE / 2;
    const cardY = trayTop + 8 + ACTION_BTN_RADIUS;

    this.nextCardBg = this.scene.add.graphics();
    this.nextCardBg.fillStyle(0x3E2723, 1);
    this.nextCardBg.fillRoundedRect(
      cardX - NEXT_CARD_SIZE / 2, cardY - NEXT_CARD_SIZE / 2,
      NEXT_CARD_SIZE, NEXT_CARD_SIZE, 14,
    );
    this.nextCardBg.lineStyle(2.5, 0xC9A96E, 0.9);
    this.nextCardBg.strokeRoundedRect(
      cardX - NEXT_CARD_SIZE / 2, cardY - NEXT_CARD_SIZE / 2,
      NEXT_CARD_SIZE, NEXT_CARD_SIZE, 14,
    );
    this.container.add(this.nextCardBg);

    this.nextLabel = this.scene.add.text(cardX, cardY + NEXT_CARD_SIZE / 2 + 4, 'NEXT', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#D4A574',
      align: 'center',
    }).setOrigin(0.5, 0).setShadow(0, 1, '#1a0e05', 2, false, true);
    this.container.add(this.nextLabel);

    // Spawn timer — full-width pill at the bottom of the tray
    const timerY = this.screenH - 30;
    const pillW = this.screenW - 40;
    const pillH = 36;

    this.spawnTimerBg = this.scene.add.graphics();
    this.spawnTimerBg.fillStyle(0x2C1B0E, 0.7);
    this.spawnTimerBg.fillRoundedRect(
      this.screenW / 2 - pillW / 2, timerY - pillH / 2,
      pillW, pillH, pillH / 2,
    );
    this.spawnTimerBg.lineStyle(1.5, 0xC9A96E, 0.4);
    this.spawnTimerBg.strokeRoundedRect(
      this.screenW / 2 - pillW / 2, timerY - pillH / 2,
      pillW, pillH, pillH / 2,
    );
    this.container.add(this.spawnTimerBg);

    this.spawnTimerText = this.scene.add.text(this.screenW / 2, timerY, '', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#F5E6D3',
    }).setOrigin(0.5, 0.5).setShadow(0, 2, '#1a0e05', 4, false, true);
    this.container.add(this.spawnTimerText);
  }

  // ------------------------------------------------------------------
  // Unified action buttons (tools + abilities in one row)
  // ------------------------------------------------------------------

  private collectActions(): ActionEntry[] {
    const actions: ActionEntry[] = [];

    // Collect unlocked tools
    if (this.toolManager) {
      const tools = this.toolManager.getAllTools();
      for (const [id, tool] of Object.entries(tools)) {
        if (this.toolManager.isToolUnlocked(id)) {
          actions.push({
            id,
            type: 'tool',
            displayName: tool.displayName,
            iconKey: tool.iconKey,
          });
        }
      }
    }

    // Collect equipped abilities
    if (this.abilityManager) {
      const equipped = this.abilityManager.getEquippedAbilities();
      for (const abilityId of equipped) {
        const config = this.abilityManager.getAbilityConfig(abilityId);
        if (!config) continue;
        actions.push({
          id: abilityId,
          type: 'ability',
          displayName: config.displayName,
          iconKey: config.iconKey,
        });
      }
    }

    return actions;
  }

  private createActionButtons(): void {
    const actions = this.collectActions();
    if (actions.length === 0) return;

    const trayTop = this.screenH - BOTTOM_BAR_HEIGHT;
    const buttonCenterY = trayTop + 8 + ACTION_BTN_RADIUS;

    // Position buttons to the right of the NEXT card, centered in remaining space
    const nextCardRight = 16 + NEXT_CARD_SIZE + 10;
    const availableWidth = this.screenW - nextCardRight - 12;
    const totalWidth = actions.length * ACTION_BTN_DIAMETER + (actions.length - 1) * ACTION_BTN_GAP;
    const startX = nextCardRight + (availableWidth - totalWidth) / 2 + ACTION_BTN_RADIUS;

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const x = startX + i * (ACTION_BTN_DIAMETER + ACTION_BTN_GAP);
      const btnContainer = this.scene.add.container(x, buttonCenterY);

      const colors = ACTION_COLORS[action.id] ?? DEFAULT_ACTION_COLOR;
      const available = this.isActionAvailable(action);

      // Outer shadow ring
      const shadow = this.scene.add.graphics();
      shadow.fillStyle(0x000000, 0.3);
      shadow.fillCircle(0, 3, ACTION_BTN_RADIUS + 2);
      btnContainer.add(shadow);

      // Main colored circle
      const bg = this.scene.add.graphics();
      if (available) {
        bg.fillStyle(colors.bg, 1);
        bg.fillCircle(0, 0, ACTION_BTN_RADIUS);
        // Inner highlight (top half shine)
        bg.fillStyle(0xffffff, 0.2);
        bg.fillCircle(0, -ACTION_BTN_RADIUS * 0.25, ACTION_BTN_RADIUS * 0.7);
        // Border
        bg.lineStyle(3, colors.border, 1);
        bg.strokeCircle(0, 0, ACTION_BTN_RADIUS);
      } else {
        bg.fillStyle(0x4a4a4a, 1);
        bg.fillCircle(0, 0, ACTION_BTN_RADIUS);
        bg.lineStyle(3, 0x333333, 1);
        bg.strokeCircle(0, 0, ACTION_BTN_RADIUS);
      }
      btnContainer.add(bg);

      // Icon
      if (this.scene.textures.exists(action.iconKey)) {
        const icon = this.scene.add.image(0, -2, action.iconKey);
        icon.setDisplaySize(48, 48);
        if (!available) icon.setAlpha(0.35);
        btnContainer.add(icon);
      }

      // Charges / uses badge (top-right)
      const badgeCount = this.getActionBadgeCount(action);
      const badgeX = ACTION_BTN_RADIUS - 4;
      const badgeY = -ACTION_BTN_RADIUS + 4;
      const badgeBg = this.scene.add.graphics();
      const badgeColor = badgeCount > 0 ? 0x22c55e : 0x991111;
      badgeBg.fillStyle(badgeColor, 1);
      badgeBg.fillCircle(badgeX, badgeY, 14);
      badgeBg.lineStyle(2, 0xffffff, 0.8);
      badgeBg.strokeCircle(badgeX, badgeY, 14);
      btnContainer.add(badgeBg);

      const badgeText = this.scene.add.text(badgeX, badgeY, `${badgeCount}`, {
        fontFamily: 'Fredoka, Arial, sans-serif',
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#ffffff',
      }).setOrigin(0.5, 0.5);
      btnContainer.add(badgeText);

      // Label below the circle
      const label = this.scene.add.text(0, ACTION_BTN_RADIUS + 5, action.displayName, {
        fontFamily: 'Fredoka, Arial, sans-serif',
        fontSize: '14px',
        fontStyle: 'bold',
        color: available ? '#F5E6D3' : '#777777',
      }).setOrigin(0.5, 0).setShadow(0, 1, '#1a0e05', 2, false, true);
      btnContainer.add(label);

      // Cooldown sweep overlay (drawn as a pie slice over the button)
      const cdOverlay = this.scene.add.graphics();
      btnContainer.add(cdOverlay);

      // Store data refs for runtime updates
      btnContainer.setData('actionId', action.id);
      btnContainer.setData('actionType', action.type);
      btnContainer.setData('bg', bg);
      btnContainer.setData('badgeBg', badgeBg);
      btnContainer.setData('badgeText', badgeText);
      btnContainer.setData('label', label);
      btnContainer.setData('colors', colors);
      btnContainer.setData('cdOverlay', cdOverlay);

      // Interactive hit area
      btnContainer.setSize(ACTION_BTN_DIAMETER, ACTION_BTN_DIAMETER);
      btnContainer.setInteractive({ useHandCursor: true });
      btnContainer.on('pointerdown', () => this.onActionButtonPressed(action));

      this.actionButtons.push(btnContainer);
      this.container.add(btnContainer);
    }
  }

  private onActionButtonPressed(action: ActionEntry): void {
    if (action.type === 'tool' && this.toolManager) {
      this.toolManager.useTool(action.id);
    } else if (action.type === 'ability' && this.abilityManager) {
      this.abilityManager.activateAbility(action.id);
    }
  }

  private isActionAvailable(action: ActionEntry): boolean {
    if (action.type === 'tool') {
      return this.toolManager?.isToolAvailable(action.id) ?? false;
    }
    return this.abilityManager?.isAbilityAvailable(action.id) ?? false;
  }

  private getActionBadgeCount(action: ActionEntry): number {
    if (action.type === 'tool') {
      return this.toolManager?.getUsesRemaining(action.id) ?? 0;
    }
    return playerState.getCharges(action.id);
  }

  // ------------------------------------------------------------------
  // Skip button (debug)
  // ------------------------------------------------------------------

  private createSkipButton(): void {
    this.skipButton = this.scene.add.text(this.screenW - 8, this.screenH - BOTTOM_BAR_HEIGHT - 12, 'SKIP', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '11px',
      fontStyle: 'bold',
      color: '#ff6666',
      backgroundColor: '#222222',
      padding: { x: 6, y: 3 },
    }).setOrigin(1, 1).setInteractive({ useHandCursor: true });

    this.skipButton.on('pointerdown', () => {
      eventBus.emit('match_end', { result: 'victory', skipped: true });
    });

    this.container.add(this.skipButton);
  }

  // ------------------------------------------------------------------
  // Update (called each frame by BattleScene)
  // ------------------------------------------------------------------

  update(
    spawnProgress: number,
    spawnNextPiece: PieceType | null,
    timerRemaining: number | null,
    spawnTimerRemaining: number,
    armyFull = false,
  ): void {
    this.updateTimer(timerRemaining);
    this.updateSpawnTimer(spawnProgress, spawnTimerRemaining, armyFull);
    this.updateSpawnPreview(armyFull ? null : spawnNextPiece);
    this.updateActionButtonStates();
  }

  // ------------------------------------------------------------------
  // Timer update
  // ------------------------------------------------------------------

  private updateTimer(remaining: number | null): void {
    if (remaining === null) {
      this.timerText.setVisible(false);
      this.timerBg.setVisible(false);
      return;
    }

    this.timerText.setVisible(true);
    this.timerBg.setVisible(true);
    const mins = Math.floor(remaining / 60);
    const secs = Math.floor(remaining % 60);
    const display = mins > 0
      ? `${mins}:${secs.toString().padStart(2, '0')}`
      : `0:${secs.toString().padStart(2, '0')}`;
    this.timerText.setText(display);

    if (remaining <= TIMER_FLASH_THRESHOLD && remaining > 0) {
      if (!this.timerFlashing) {
        this.timerFlashing = true;
        this.redrawTimerCapsule(0xC62828, 0x8B1A1A, 0xEF5350);
        this.timerText.setStroke('#8B1A1A', 4);
        this.scene.tweens.add({
          targets: this.timerText,
          alpha: { from: 1, to: 0.3 },
          yoyo: true,
          repeat: -1,
          duration: 400,
        });
      }
    } else if (this.timerFlashing && remaining > TIMER_FLASH_THRESHOLD) {
      this.timerFlashing = false;
      this.redrawTimerCapsule(0x2E7D32, 0x1B5E20, 0x66BB6A);
      this.timerText.setStroke('#1B5E20', 4);
      this.scene.tweens.killTweensOf(this.timerText);
      this.timerText.setAlpha(1);
    }
  }

  private redrawTimerCapsule(fill: number, border: number, highlight: number): void {
    const centerY = TOP_BAR_HEIGHT / 2 + 6;
    const pillW = 110;
    const pillH = 46;
    const pillX = this.screenW - 14 - pillW;

    this.timerBg.clear();
    // Drop shadow
    this.timerBg.fillStyle(0x000000, 0.4);
    this.timerBg.fillRoundedRect(pillX + 1, centerY - pillH / 2 + 3, pillW, pillH, pillH / 2);
    // Main fill
    this.timerBg.fillStyle(fill, 1);
    this.timerBg.fillRoundedRect(pillX, centerY - pillH / 2, pillW, pillH, pillH / 2);
    // Border
    this.timerBg.lineStyle(3, border, 1);
    this.timerBg.strokeRoundedRect(pillX, centerY - pillH / 2, pillW, pillH, pillH / 2);
    // Inner highlight shine
    this.timerBg.fillStyle(highlight, 0.3);
    this.timerBg.fillRoundedRect(pillX + 5, centerY - pillH / 2 + 4, pillW - 10, pillH / 2 - 4, { tl: pillH / 2, tr: pillH / 2, bl: 0, br: 0 });
  }

  // ------------------------------------------------------------------
  // Spawn timer update (simple text, no progress bar)
  // ------------------------------------------------------------------

  private updateSpawnTimer(progress: number, timeRemaining: number, armyFull: boolean): void {
    if (armyFull) {
      this.spawnTimerText.setText('ARMY FULL');
      this.spawnTimerText.setColor('#f59e0b');
      return;
    }

    const isReady = progress >= 1;

    if (isReady) {
      this.spawnTimerText.setText('READY!');
      this.spawnTimerText.setColor('#4ade80');
    } else {
      const secs = Math.ceil(timeRemaining);
      this.spawnTimerText.setText(`${secs}s`);
      this.spawnTimerText.setColor('#F5E6D3');
    }
  }

  // ------------------------------------------------------------------
  // Spawn preview update
  // ------------------------------------------------------------------

  private updateSpawnPreview(nextPiece: PieceType | null): void {
    if (this.spawnPreviewIcon) {
      this.spawnPreviewIcon.destroy();
      this.spawnPreviewIcon = null;
    }

    if (!nextPiece) return;

    const trayTop = this.screenH - BOTTOM_BAR_HEIGHT;
    const cardX = 16 + NEXT_CARD_SIZE / 2;
    const cardY = trayTop + 8 + ACTION_BTN_RADIUS;
    const iconKey = `piece_${nextPiece.toLowerCase()}_blue`;

    if (this.scene.textures.exists(iconKey)) {
      this.spawnPreviewIcon = this.scene.add.image(cardX, cardY, iconKey);
      this.spawnPreviewIcon.setDisplaySize(54, 54);
      this.container.add(this.spawnPreviewIcon);
    }
  }

  // ------------------------------------------------------------------
  // Action button state updates
  // ------------------------------------------------------------------

  private updateActionButtonStates(): void {
    for (const btn of this.actionButtons) {
      const actionId = btn.getData('actionId') as string;
      const actionType = btn.getData('actionType') as 'tool' | 'ability';
      const bg = btn.getData('bg') as Phaser.GameObjects.Graphics;
      const badgeBg = btn.getData('badgeBg') as Phaser.GameObjects.Graphics;
      const badgeText = btn.getData('badgeText') as Phaser.GameObjects.Text;
      const label = btn.getData('label') as Phaser.GameObjects.Text;
      const colors = btn.getData('colors') as { bg: number; border: number };

      let available = false;
      let count = 0;

      if (actionType === 'tool' && this.toolManager) {
        available = this.toolManager.isToolAvailable(actionId);
        count = this.toolManager.getUsesRemaining(actionId);
      } else if (actionType === 'ability' && this.abilityManager) {
        available = this.abilityManager.isAbilityAvailable(actionId);
        count = playerState.getCharges(actionId);
      }

      // Redraw background circle based on availability
      bg.clear();
      if (available) {
        bg.fillStyle(colors.bg, 1);
        bg.fillCircle(0, 0, ACTION_BTN_RADIUS);
        bg.fillStyle(0xffffff, 0.2);
        bg.fillCircle(0, -ACTION_BTN_RADIUS * 0.25, ACTION_BTN_RADIUS * 0.7);
        bg.lineStyle(3, colors.border, 1);
        bg.strokeCircle(0, 0, ACTION_BTN_RADIUS);
      } else {
        bg.fillStyle(0x4a4a4a, 1);
        bg.fillCircle(0, 0, ACTION_BTN_RADIUS);
        bg.lineStyle(3, 0x333333, 1);
        bg.strokeCircle(0, 0, ACTION_BTN_RADIUS);
      }

      // Update badge
      const badgeX = ACTION_BTN_RADIUS - 6;
      const badgeY = -ACTION_BTN_RADIUS + 6;
      badgeBg.clear();
      const badgeColor = count > 0 ? 0x22c55e : 0x991111;
      badgeBg.fillStyle(badgeColor, 1);
      badgeBg.fillCircle(badgeX, badgeY, 13);
      badgeBg.lineStyle(2, 0xffffff, 0.8);
      badgeBg.strokeCircle(badgeX, badgeY, 13);

      badgeText.setText(`${count}`);
      label.setColor(available ? '#F5E6D3' : '#777777');

      // Draw cooldown sweep overlay for abilities
      const cdOverlay = btn.getData('cdOverlay') as Phaser.GameObjects.Graphics;
      if (cdOverlay) {
        cdOverlay.clear();
        if (actionType === 'ability' && this.abilityManager) {
          const frac = this.abilityManager.getCooldownFraction(actionId);
          if (frac > 0) {
            cdOverlay.fillStyle(0x000000, 0.55);
            const startAngle = -Math.PI / 2;
            const endAngle = startAngle + frac * Math.PI * 2;
            cdOverlay.slice(0, 0, ACTION_BTN_RADIUS, startAngle, endAngle, false);
            cdOverlay.fillPath();
          }
        }
      }

      btn.setAlpha(available ? 1 : 0.5);
    }
  }

  // ------------------------------------------------------------------
  // Dynamic ability button management (public API kept for battleScene)
  // ------------------------------------------------------------------

  /**
   * Get the screen-space bounds of an ability/action button by ID.
   * Used by the tutorial spotlight system.
   */
  getAbilityButtonBounds(abilityId: string): Phaser.Geom.Rectangle | null {
    for (const btn of this.actionButtons) {
      if (btn.getData('actionId') === abilityId) {
        const worldX = btn.x + this.container.x;
        const worldY = btn.y + this.container.y;
        return new Phaser.Geom.Rectangle(
          worldX - ACTION_BTN_RADIUS,
          worldY - ACTION_BTN_RADIUS,
          ACTION_BTN_DIAMETER,
          ACTION_BTN_DIAMETER,
        );
      }
    }
    return null;
  }

  /**
   * Rebuild action buttons from current playerState.
   * Called when abilities are granted mid-match (e.g., tutorial).
   */
  rebuildAbilityButtons(): void {
    for (const btn of this.actionButtons) {
      btn.destroy();
    }
    this.actionButtons = [];

    if (this.abilityManager) {
      this.abilityManager.refreshEquippedAbilities();
    }

    this.createActionButtons();
  }

  // ------------------------------------------------------------------
  // Bottom bar visibility (hidden during early tutorial)
  // ------------------------------------------------------------------

  setBottomBarVisible(visible: boolean): void {
    const alpha = visible ? 1 : 0;
    this.bottomPanel.setAlpha(alpha);
    this.spawnTimerBg.setAlpha(alpha);
    this.spawnTimerText.setAlpha(alpha);
    this.nextCardBg.setAlpha(alpha);
    this.nextLabel.setAlpha(alpha);
    if (this.spawnPreviewIcon) this.spawnPreviewIcon.setAlpha(alpha);
    for (const btn of this.actionButtons) {
      btn.setAlpha(alpha);
    }
  }

  // ------------------------------------------------------------------
  // Cleanup
  // ------------------------------------------------------------------

  destroy(): void {
    if (this.timerFlashing) {
      this.scene.tweens.killTweensOf(this.timerText);
    }
    this.container.destroy();
  }
}
